import env from 'config/env';
import Error from './error/error-page';
import fetch from 'app/utilities/fetch';
import { getNewsTemplateType } from 'app/utilities/news-listing';
import Helmet from 'react-helmet';
import Loader from 'app/components/partials/loader';
import NotFound from './error/not-found-page';
import { POST_MESSAGE_TYPE } from 'app/utilities/post-message-type';
import selectGlobalFields from 'app/selectors/global-fields';
import Template from 'app/components/templates/index';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ENDPOINTS, RESPONSE } from 'config/api';
import React, { useCallback, useEffect, useState } from 'react';

const STATUS = {
    LOADING: 'loading',
    READY: 'ready',
    NOT_FOUND: 'not-found',
    ERROR: 'error'
};

/**
 * Renders whatever the CMS live page builder is previewing (a page, an
 * animal, a news article or a news category) inside an iframe.
 *
 * Unlike ContentPage this does not go through the normal page-data pipeline:
 * `pre-route` and the server side render both leave `/livepreview/:id` alone
 * (see `isFrontendRoute`/`server/app.js`), so this component fetches its own
 * data instead of relying on the store.
 *
 * The CMS asks this page to refetch when the entity has changed
 * (`RELOAD_PAGE_DATA`). Selecting, highlighting and editing individual content
 * blocks is handled by the ContentBlocks component, which knows it is in the
 * live preview from the `/livepreview/` path.
 *
 * The listing/category context a real news category page gets from its own
 * URL (`activeCategories`, `newsCategories`, `newsSubCategories`, etc.) has no
 * equivalent here, so it is left out: the nav/feature sections that depend on
 * it render as nothing, while the editable content and the real article list
 * (via `FilterStories`' own fetch, keyed off this entity's id) still show.
 *
 * @return {object} JSX
 */
const LivePreview = () => {
    const { id } = useParams();
    const globalFields = useSelector(selectGlobalFields);
    const [page, setPage] = useState(null);
    const [status, setStatus] = useState(STATUS.LOADING);

    // Fetch the entity from API live preview endpoint
    const loadPreviewData = useCallback(() => {
        return fetch(ENDPOINTS.LIVE_PREVIEW(id))
            .then((response) => {
                if (!response || !response.data) {
                    setStatus(STATUS.NOT_FOUND);

                    return;
                }

                setPage(response.data);
                setStatus(STATUS.READY);
            })
            .catch((apiError) => {
                setStatus(apiError && apiError.status === RESPONSE.NOT_FOUND ? STATUS.NOT_FOUND : STATUS.ERROR);
            });
    }, [id]);

    useEffect(() => {
        loadPreviewData();

        // CMS will send us update message when it wants us to refresh
        const handleUpdate = (event) => {
            // Make sure to validate the origin for security
            if (event.origin !== env.CMS_DOMAIN) return;

            if (event.data && event.data.type === POST_MESSAGE_TYPE.RELOAD_PAGE_DATA) {
                loadPreviewData();
            }
        };

        window.addEventListener('message', handleUpdate);

        return () => {
            window.removeEventListener('message', handleUpdate);
        };
    }, [loadPreviewData]);

    if (status === STATUS.NOT_FOUND) return <NotFound />;
    if (status === STATUS.ERROR) return <Error />;
    if (!page) return <Loader />;

    const { attributes, id: entityId, type } = page;
    const { level, name: title, parent, slug } = attributes;
    const { new_ticket_flow_slug: newTicketFlowSlug } = globalFields;

    let templateTypeToUse; // eslint-disable-line one-var
    if (slug === newTicketFlowSlug) {
        templateTypeToUse = 'tickets';
    } else if (slug === 'day-pass') {
        templateTypeToUse = 'day-pass';
    } else {
        templateTypeToUse = getNewsTemplateType(type, slug, level, parent);
    }

    return (
        <main role="main" className="main">
            <Helmet>
                <meta name="robots" content="noindex, nofollow" />
                <title>{title ? `${title} - Live Preview` : 'Live Preview'}</title>
            </Helmet>
            <Template
                type={templateTypeToUse}
                data={attributes}
                globalFields={globalFields}
                id={String(entityId)}
                listingPage={1}
            />
        </main>
    );
};

export default LivePreview;
